/**
 * Empire V13 PDP orchestrator (Zeckos custom replacement of section-static-product)
 *
 * WHY THIS EXISTS (V13):
 * On Empire V13, clicking a variant swatch fires <variant-selection>'s
 * `variant-change` event and updates the form's hidden [name="id"], but V13's
 * native handler does NOT update the price, the ?variant= URL, or the gallery
 * on our variation products (verified live 2026-06-17: selecting a color updated
 * NOTHING — price/activeIndex/URL all frozen). Standalone products are fine;
 * only multi-variant products need this. The V13-first plan had assumed native
 * `_switchVariant` replaced this file — it doesn't on our data (source-present
 * != working). See ADR-082 + handoff 2026-06-17-v13-migration-variant-blockers.md.
 *
 * WHAT IT DOES on each variant-change:
 *   1. Gallery — shows ONLY the selected variant's images. Per-variant image
 *      sets are encoded in the alt-text convention "{Color} - Image N - ..."
 *      (Shopify can't store per-variant image *sets* via image.variant_ids —
 *      ADR-082), so we filter client-side. The old display:none approach broke
 *      Empire's Swiper (hidden slides stay in Swiper's absolute-index space →
 *      thumbnail clicks land on blanks + active-thumb desync — known-patterns
 *      2026-05-15). Instead we REBUILD the gallery's slide DOM (main viewer +
 *      thumbnail strip, in lockstep) to only the matching media, then
 *      detach+re-attach <product-gallery> so Empire RE-INITIALIZES a fresh
 *      Swiper over dense indices. V13's ProductGallery.connectedCallback is
 *      re-init-safe (it nulls swiper/thumbnailsSwiper, re-reads initial-slide,
 *      rebuilds via #onResize) and disconnectedCallback fully tears down
 *      (#destroySwiper/#destroyThumbnails/lightbox/hover-zoom). Empire re-wires
 *      its own thumbnail clicks against the rebuilt DOM, so there is no index
 *      desync to patch around.
 *   2. Price — [data-price] / [data-price-compare] / range elements.
 *   3. Add-to-cart — enabled state + label per variant.available (ctx i18n).
 *   4. URL — history.replaceState ?variant={id}.
 *   5. SKU — [data-product-sku].
 *
 * Affinity (an image belongs to a variant), checked in order:
 *   1. FILENAME → SKU → option1. The Zeckos image-naming convention encodes the
 *      owning child SKU in the filename (e.g. ZKO-99223WD-LB-wood-wheel-….jpg).
 *      This is unambiguous and survives generic/empty/comma alts — the strongest
 *      signal — and is matched longest-SKU-first with alphanumeric boundaries so
 *      "ZKO-99223" can't match inside "ZKO-99223WD-LB".
 *   2. ALT-prefix "{option1} - Image N - …" matched LONGEST-VALUE-FIRST (so
 *      "Wood" can't shadow "Wood And Metal") with delimiters space/-/:/comma.
 * Images with no recognized signal (e.g. a generic "COLOR" parent shot) belong to
 * no variant and are HIDDEN once a variant is selected (operator decision
 * 2026-06-17). Failsafe: if a variant matches zero images, show all of them
 * (degraded but never blank — e.g. an older listing not yet re-imaged).
 * NB: this is CLIENT-SIDE filtering only — it never writes image.variant_ids
 * (one-image-per-variant featured-image pointer; ADR-082), so it is immune to
 * Channel Advisor resyncs (CA cannot touch theme JS). 2026-06-26 bleed fix.
 *
 * Single-axis: filtering keys on option1 (Color). Secondary options (Size, etc.)
 * do not gate image visibility.
 *
 * Maintenance: Zeckos custom replacement of Pixel Union's section-static-product.
 * On an Empire upgrade, re-verify V13's gallery internals (ProductGallery
 * connectedCallback re-init-safety; .product-gallery__viewer / __navigation
 * structure; that native switching still no-ops) BEFORE trusting this — see
 * memory feedback_verify_native_behavior_before_deleting_custom.
 */

(() => {
  const moneyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  const money = (cents) => moneyFormatter.format((cents || 0) / 100);

  function init() {
    const variantSelection = document.querySelector('variant-selection');
    if (!variantSelection) return; // standalone / no options — nothing to orchestrate

    const productForm = document.querySelector('form[action*="/cart/add"]');
    const addToCartBtn = productForm
      ? productForm.querySelector('button[name="add"], [type="submit"]')
      : null;

    // Section script-tag JSON — the static-product section injects product data,
    // i18n strings, and settings. Filter by data-section-type="static-product"
    // (the header/footer emit their own data-section-data tags with no product).
    const sectionDataEl = document.querySelector('script[data-section-data][data-section-type="static-product"]');
    let sectionData = {};
    try { sectionData = JSON.parse(sectionDataEl?.textContent || '{}'); } catch (_) {}
    const ctx = sectionData.context || {};
    const product = sectionData.product || null;

    // Effectively-single-variant products (1 variant, or only 1 in stock/available) should
    // present like a standalone: hide the lone variant selector. The server already renders
    // that variant's content (selected_or_first_available_variant). Runs BEFORE the
    // length<=1 bail so genuinely-single-variant products get the selector hidden too. The
    // <select name="id"> inside stays in the form (display:none still submits the id).
    const availableCount = (product && Array.isArray(product.variants))
      ? product.variants.filter((v) => v && v.available).length
      : 0;
    if (availableCount <= 1) variantSelection.style.display = 'none';

    // Single-variant / standalone products need no orchestration — V13 native is
    // correct for them. Bail BEFORE touching the gallery so we never do a needless
    // rebuild on the bulk of the catalog (some single-variant products still render
    // a <variant-selection> shell with zero swatches, so the element check above
    // isn't enough — gate on the actual variant count).
    if (!product || !Array.isArray(product.variants) || product.variants.length <= 1) return;

    // Collect option1 values for alt-prefix matching (single-axis on option1).
    const optionValuesSet = new Set();
    if (product && Array.isArray(product.variants)) {
      for (const v of product.variants) if (v.option1) optionValuesSet.add(String(v.option1));
    }
    const optionValues = Array.from(optionValuesSet);
    // LONGEST option value FIRST: a value that is a boundary-prefix of another
    // ("Wood" ⊂ "Wood And Metal") must not greedily shadow the longer one. The
    // original short-first loop mis-assigned every "Wood And Metal - Image N" alt
    // to the "Wood" variant → the in-stock "Wood And Metal" variant matched zero
    // images → failsafe showed ALL finishes (the bleed). ZKO-99223 was the live
    // repro (2026-06-26). Sorting longest-first makes the most-specific value win.
    const optionValuesByLen = optionValues.slice().sort((a, b) => b.length - a.length);

    // Filename→SKU→option1 backbone. The Zeckos image-naming convention encodes
    // the OWNING child SKU in the filename (e.g. ZKO-99223WD-LB-wood-wheel-….jpg),
    // which is an UNAMBIGUOUS per-variant signal even when the alt-text is generic
    // ("COLOR,Size"), empty, or comma-formatted — the cases that defeat alt-prefix
    // matching. We map filename→SKU→option1 so a filename hit produces the same
    // affinity string the alt path would. SKUs longest-first + an alphanumeric
    // boundary so "ZKO-99223" can't match inside "ZKO-99223WD-LB". Client-side
    // only — it never writes image.variant_ids (that is a one-image-per-variant
    // featured-image pointer that MOVES the featured image; ADR-082), so it is
    // immune to CA resyncs (CA can't touch theme JS).
    const skuToOption1 = new Map();
    const skusByLen = [];
    if (product && Array.isArray(product.variants)) {
      for (const v of product.variants) {
        if (v.sku && v.option1) {
          skuToOption1.set(String(v.sku).toLowerCase(), String(v.option1));
          skusByLen.push(String(v.sku).toLowerCase());
        }
      }
    }
    skusByLen.sort((a, b) => b.length - a.length); // most-specific SKU wins

    function isAlnum(code) {
      return (code >= 48 && code <= 57) || (code >= 97 && code <= 122); // 0-9 a-z (lowercased)
    }
    function option1FromFilename(urlStr) {
      if (!urlStr) return null;
      const s = String(urlStr).toLowerCase();
      for (const sku of skusByLen) {
        let from = 0, idx;
        while ((idx = s.indexOf(sku, from)) !== -1) {
          const after = s.charCodeAt(idx + sku.length); // NaN at string end → boundary OK
          const before = idx === 0 ? -1 : s.charCodeAt(idx - 1);
          // Require a non-alphanumeric (or edge) boundary on BOTH sides so a short
          // SKU can't match inside a longer one (ZKO-99223 vs ZKO-99223WD-LB).
          if (!isAlnum(after) && (idx === 0 || !isAlnum(before))) {
            return skuToOption1.get(sku);
          }
          from = idx + 1;
        }
      }
      return null;
    }

    function getMediaAffinity(altText, urlStr) {
      // 1) Filename-SKU (strongest, unambiguous, CA-resync-proof).
      const byFile = option1FromFilename(urlStr);
      if (byFile) return byFile;
      // 2) Alt-prefix "{OptionValue} - Image N - …", longest-value-first; `,`
      //    added to the delimiter set for the "Multicolored, 2"-style alts.
      if (altText) {
        const a = String(altText).trim().toLowerCase();
        for (const v of optionValuesByLen) {
          const vl = v.toLowerCase();
          if (a === vl) return v;
          if (a.startsWith(vl + ' ')) return v;
          if (a.startsWith(vl + '-')) return v;
          if (a.startsWith(vl + ':')) return v;
          if (a.startsWith(vl + ',')) return v;
        }
      }
      return null; // no recognized signal → shared / parent shot
    }

    // Collect every URL-bearing attribute on a slide into one string so the
    // filename (which carries the SKU) is found regardless of which attribute the
    // theme/lazy-loader populates (src / srcset / data-src / photoswipe / zoom).
    function slideUrlString(slideEl) {
      if (!slideEl) return '';
      const parts = [];
      const img = slideEl.querySelector('img');
      if (img) {
        parts.push(img.getAttribute('src') || '');
        parts.push(img.getAttribute('data-src') || '');
        parts.push(img.getAttribute('srcset') || '');
        parts.push(img.getAttribute('data-srcset') || '');
        try { if (img.currentSrc) parts.push(img.currentSrc); } catch (_) {}
      }
      slideEl.querySelectorAll('[data-photoswipe-src], [data-zoom], a[href]').forEach((el) => {
        parts.push(el.getAttribute('data-photoswipe-src') || '');
        parts.push(el.getAttribute('data-zoom') || '');
        parts.push(el.getAttribute('href') || '');
      });
      return parts.join(' ');
    }

    // Schedule a callback at the next paint when the tab is visible (smooth,
    // batched with layout) but via a timer when it's HIDDEN — requestAnimationFrame
    // is paused in background tabs, so without this fallback the gallery filter
    // would silently never run for a PDP opened in a background tab (price/cart
    // still update because those run synchronously). Verified 2026-06-17.
    function nextFrame(cb) {
      if (document.hidden) setTimeout(cb, 0);
      else requestAnimationFrame(cb);
    }

    // ───────────────── Gallery: rebuild filtered slides + re-attach ─────────────────
    const MAIN_WRAP_SEL = '.product-gallery__viewer .swiper-wrapper';
    const NAV_WRAP_SEL = '.product-gallery__navigation .swiper-wrapper';
    let galleryCache = null; // { mains: [{affinity, node}], thumbs: [node], parallel: bool }

    function getGallery() { return document.querySelector('product-gallery'); }

    // Clone a slide and strip Swiper's runtime classes + inline layout styles so
    // the rebuilt DOM is pristine and Empire's re-init computes everything fresh.
    function cleanSlideClone(el) {
      const c = el.cloneNode(true);
      const keep = (cl) => cl === 'swiper-slide' || !cl.startsWith('swiper-');
      c.className = (c.className || '').split(/\s+/).filter(Boolean).filter(keep).join(' ');
      if (!c.classList.contains('swiper-slide')) c.classList.add('swiper-slide');
      c.style.cssText = '';
      c.removeAttribute('data-swiper-slide-index');
      return c;
    }

    // Snapshot the FULL pristine slide set once, before any filtering. Main slides
    // carry the alt (→ affinity); thumbnails are 1:1 parallel by media order
    // (verified live) so they're kept by the same index set as the main slides.
    function buildGalleryCache(pg) {
      const mainWrap = pg.querySelector(MAIN_WRAP_SEL);
      if (!mainWrap) return null;
      const navWrap = pg.querySelector(NAV_WRAP_SEL);
      const mainEls = Array.from(mainWrap.querySelectorAll(':scope > .swiper-slide'));
      const thumbEls = navWrap ? Array.from(navWrap.querySelectorAll(':scope > .swiper-slide')) : [];
      const mains = mainEls.map((s) => {
        const img = s.querySelector('img');
        return { affinity: getMediaAffinity(img && img.alt, slideUrlString(s)), node: cleanSlideClone(s) };
      });
      const thumbs = thumbEls.map(cleanSlideClone);
      // Only filter thumbs in lockstep when counts match; otherwise leave thumbs
      // alone (never desync main vs thumb counts → never re-introduce the index bug).
      const parallel = thumbs.length === mains.length && mains.length > 0;
      return { mains, thumbs, parallel };
    }

    function applyGalleryFilter(variant) {
      const pg = getGallery();
      if (!pg || !variant) return;
      if (pg.hasAttribute('placeholder')) return; // no media
      if (!galleryCache) galleryCache = buildGalleryCache(pg);
      if (!galleryCache || !galleryCache.mains.length) return;

      const target = variant.option1 ? String(variant.option1) : null;
      let keep = [];
      galleryCache.mains.forEach((m, i) => { if (target && m.affinity === target) keep.push(i); });
      if (!keep.length) keep = galleryCache.mains.map((_, i) => i); // failsafe: show all

      // Map the variant's featured media to its position within the filtered set.
      let initialSlide = 0;
      if (variant.featured_media && variant.featured_media.position) {
        const origIdx = variant.featured_media.position - 1; // cache index === media order
        const k = keep.indexOf(origIdx);
        if (k >= 0) initialSlide = k;
      }

      const parent = pg.parentNode;
      const sib = pg.nextSibling;
      try {
        pg.style.visibility = 'hidden'; // hide the teardown/rebuild churn
        // Detach → Empire's disconnectedCallback destroys swiper/thumbnails/etc.
        pg.remove();

        // Rebuild main + nav wrappers in lockstep from the pristine cache.
        const mainWrap = pg.querySelector(MAIN_WRAP_SEL);
        const navWrap = pg.querySelector(NAV_WRAP_SEL);
        if (mainWrap) {
          mainWrap.textContent = '';
          keep.forEach((i) => mainWrap.appendChild(galleryCache.mains[i].node.cloneNode(true)));
        }
        if (navWrap && galleryCache.parallel) {
          navWrap.textContent = '';
          keep.forEach((i) => { if (galleryCache.thumbs[i]) navWrap.appendChild(galleryCache.thumbs[i].cloneNode(true)); });
        }
        pg.setAttribute('initial-slide', String(initialSlide));

        // Re-attach → connectedCallback → #onResize builds a fresh Swiper and
        // re-wires Empire's own thumbnail clicks over dense indices.
        if (parent) parent.insertBefore(pg, sib);
      } finally {
        // Always restore visibility (after re-init has a couple of frames to settle).
        nextFrame(() => nextFrame(() => {
          const g = getGallery();
          if (g) g.style.visibility = '';
        }));
      }
    }

    // Coalesce rapid variant clicks to a single rebuild per animation frame.
    let pendingVariant = null;
    let galleryScheduled = false;
    function scheduleGalleryFilter(variant) {
      pendingVariant = variant;
      if (galleryScheduled) return;
      galleryScheduled = true;
      nextFrame(() => {
        galleryScheduled = false;
        const v = pendingVariant; pendingVariant = null;
        try { applyGalleryFilter(v); } catch (e) { console.warn('section-static-product: gallery filter failed', e); }
      });
    }

    // Wait until Empire's gallery has finished its first init before the initial
    // filter (its #onResize build can lag DOMContentLoaded). Tearing down a
    // half-built swiper risks a throw; this avoids the race. Caps at ~0.5s.
    function whenGalleryReady(cb, tries = 0) {
      const pg = getGallery();
      if (!pg || pg.hasAttribute('placeholder') || pg.swiper || tries > 60) return cb();
      nextFrame(() => whenGalleryReady(cb, tries + 1));
    }

    // ───────────────── Price / Add-to-cart / URL / SKU ─────────────────
    function updatePrice(variant) {
      if (!variant) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      document.querySelectorAll('[data-price]').forEach((el) => {
        el.textContent = money(variant.price);
        el.style.display = '';
      });
      document.querySelectorAll('[data-price-min], [data-price-max]').forEach((el) => {
        el.style.display = 'none';
      });
      document.querySelectorAll('.price--varies').forEach((el) => {
        el.classList.remove('price--varies');
      });

      document.querySelectorAll('[data-price-compare-container]').forEach((c) => {
        c.classList.toggle('visible', !!onSale);
      });
      document.querySelectorAll('[data-price-compare]').forEach((el) => {
        if (onSale) { el.textContent = money(variant.compare_at_price); el.style.display = ''; }
        else { el.textContent = ''; }
      });
      document.querySelectorAll('[data-price-compare-min], [data-price-compare-max]').forEach((el) => {
        el.style.display = 'none';
      });
      document.querySelectorAll('.price__current').forEach((el) => {
        el.classList.toggle('price__current--on-sale', !!onSale);
      });
    }

    function setButtonLabel(btn, label) {
      if (!btn || !label) return;
      const span = btn.querySelector('.button__text, .product-form__submit-text, span');
      if (span) span.textContent = label;
      else btn.textContent = label;
    }

    function updateAddToCart(variant) {
      if (!addToCartBtn) return;
      if (variant && variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.removeAttribute('aria-disabled');
        addToCartBtn.classList.remove('disabled');
        if (ctx.product_available) setButtonLabel(addToCartBtn, ctx.product_available);
      } else if (variant && !variant.available) {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('disabled');
        if (ctx.product_sold_out) setButtonLabel(addToCartBtn, ctx.product_sold_out);
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.setAttribute('aria-disabled', 'true');
        addToCartBtn.classList.add('disabled');
        if (ctx.product_unavailable) setButtonLabel(addToCartBtn, ctx.product_unavailable);
      }
    }

    function updateUrl(variant) {
      if (!variant) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      } catch (_) { /* non-fatal */ }
    }

    function updateSku(variant) {
      // Empire renders the child SKU server-side from the URL variant, so it only
      // "changed on refresh" before. variant.sku is on every variant object.
      if (!variant || !variant.sku) return;
      document.querySelectorAll('[data-product-sku]').forEach((el) => {
        el.textContent = variant.sku;
      });
    }

    // Per-variant content (ZeckosHub Phase G, ADR-052). Swap the H1 title, the description
    // box, and the bullets to the selected variant's content. The map (variantId -> {title,
    // descriptionHtml, bullets}) is server-emitted into the section JSON (product | json omits
    // variant metafields). The server already rendered the selected/first variant's content;
    // this keeps it in sync on variant-change. Variants with no entry fall back to product-level.
    // Synchronous text/HTML swap — no rAF (it doesn't touch gallery layout). Hooks always exist
    // in the DOM (bullets <ul hidden>), so we can populate when switching from a no-content variant.
    function updateContent(variant) {
      if (!variant) return;
      const map = sectionData.variant_content || {};
      const vc = map[variant.id] || null;

      const titleEl = document.querySelector('h1.product-title');
      if (titleEl) titleEl.textContent = (vc && vc.title) ? vc.title : (product.title || '');

      const descEl = document.querySelector('[data-variant-description]');
      if (descEl) descEl.innerHTML = (vc && vc.descriptionHtml) ? vc.descriptionHtml : (product.description || '');

      // Prominent key_features block (main column): show the selected variant's bullets,
      // or restore the parent key_features HTML (sectionData.parent_keyfeatures) when the
      // variant has none. parent_keyfeatures is our own server-emitted metafield HTML.
      const kfEl = document.querySelector('[data-variant-keyfeatures]');
      if (kfEl) {
        const bullets = (vc && Array.isArray(vc.bullets)) ? vc.bullets : [];
        if (bullets.length) {
          const ul = document.createElement('ul');
          ul.className = 'zh-variant-keyfeatures';
          bullets.forEach((b) => { const li = document.createElement('li'); li.textContent = b; ul.appendChild(li); });
          kfEl.innerHTML = '';
          kfEl.appendChild(ul);
        } else {
          kfEl.innerHTML = sectionData.parent_keyfeatures || '';
        }
      }

      // Product Details spec table — rebuild from the variant's specs, or restore the
      // product-level fallback (<template>) when the variant has no specs (e.g. a SKU not in
      // the DDB CA-mirror). textContent escapes the values; rows are operator-confirmed order.
      const specsBody = document.querySelector('[data-variant-specs]');
      if (specsBody) {
        const specs = vc && vc.specs;
        if (specs) {
          const rows = [];
          const add = (label, val) => { if (val !== undefined && val !== null && val !== '') rows.push([label, String(val)]); };
          add('Item Type', specs.type);
          add('Material', specs.material);
          add('Color', specs.color);
          add('Dimensions', specs.dimensions);
          add('Weight', specs.weight);
          if (specs.setCount) add('Set', 'Set of ' + specs.setCount);
          add('Style', specs.style);
          add('Theme', specs.theme);
          add('Design', specs.design);
          add('Assembly Required', specs.assembly);
          specsBody.innerHTML = '';
          rows.forEach((pair) => {
            const tr = document.createElement('tr');
            const th = document.createElement('th'); th.setAttribute('scope', 'row'); th.textContent = pair[0];
            const td = document.createElement('td'); td.textContent = pair[1];
            tr.appendChild(th); tr.appendChild(td); specsBody.appendChild(tr);
          });
        } else {
          const tpl = document.querySelector('[data-variant-specs-fallback]');
          if (tpl) specsBody.innerHTML = tpl.innerHTML;
        }
      }
    }

    function applyVariant(variant) {
      try { scheduleGalleryFilter(variant); } catch (e) { console.warn('section-static-product: scheduleGalleryFilter failed', e); }
      try { updatePrice(variant); }            catch (e) { console.warn('section-static-product: updatePrice failed', e); }
      try { updateAddToCart(variant); }        catch (e) { console.warn('section-static-product: updateAddToCart failed', e); }
      try { updateContent(variant); }          catch (e) { console.warn('section-static-product: updateContent failed', e); }
      try { updateSku(variant); }              catch (e) { console.warn('section-static-product: updateSku failed', e); }
      try { updateUrl(variant); }              catch (e) { console.warn('section-static-product: updateUrl failed', e); }
    }

    // Live wiring — V13 fires `variant-change` on <variant-selection> with
    // { detail: { variant } } (verified live). Native does nothing with it.
    variantSelection.addEventListener('variant-change', (event) => {
      const v = (event.detail || {}).variant;
      if (v) applyVariant(v);
      else updateAddToCart(null); // option combo with no matching variant
    });

    // Initial sync — match the URL's ?variant= (or first available variant) so the
    // page state (price, gallery filter, ATC, SKU) matches the URL on load.
    function initialVariant() {
      if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
      const urlVariantId = new URLSearchParams(window.location.search).get('variant');
      if (urlVariantId) {
        const match = product.variants.find((v) => String(v.id) === urlVariantId);
        if (match) return match;
      }
      return product.variants.find((v) => v.available) || product.variants[0];
    }

    const initial = initialVariant();
    if (initial) {
      whenGalleryReady(() => applyVariant(initial));
    }

    // Reserve the title block's height to the TALLEST variant title so switching variants
    // doesn't reflow the H1 and shove the swatches/price down ("chasing swatches",
    // operator-reported 2026-06-18). Measured once against the real rendered container width.
    // Only when the selector is actually switchable (availableCount > 1).
    function stabilizeTitleHeight() {
      const titleEl = document.querySelector('h1.product-title');
      if (!titleEl) return;
      const map = sectionData.variant_content || {};
      const titles = Object.keys(map).map((k) => map[k] && map[k].title).filter(Boolean);
      if (titles.length < 2) return;
      const original = titleEl.textContent;
      let maxH = titleEl.getBoundingClientRect().height;
      titles.forEach((t) => {
        titleEl.textContent = t;
        const h = titleEl.getBoundingClientRect().height;
        if (h > maxH) maxH = h;
      });
      titleEl.textContent = original;
      if (maxH > 0) titleEl.style.minHeight = Math.ceil(maxH) + 'px';
    }
    if (availableCount > 1) {
      try { stabilizeTitleHeight(); } catch (e) { console.warn('section-static-product: stabilizeTitleHeight failed', e); }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
